



import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthModal = ({ isOpen, onClose, initialForm = 'login' }) => {
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [welcomePopup, setWelcomePopup] = useState({ show: false, name: '', role: '' });

  const [loginData, setLoginData] = useState({
    staffId: "",
    password: "",
  });

  const [signupData, setSignupData] = useState({
    name: "",
    staffId: "",
    password: "",
    confirmPassword: "",
    role: "employee"
  });

  // Role-based credentials for demo
  const roleCredentials = {
    superadmin: { staffId: "SUPER001", password: "super@123", role: "Super Admin" },
    admin: { staffId: "ADMIN001", password: "admin@1234", role: "Admin" },
    manager: { staffId: "MGR001", password: "manager@123", role: "Manager" },
    hr: { staffId: "HR001", password: "hr@123", role: "HR" },
    employee: { staffId: "EMP001", password: "employee@123", role: "Employee" }
  };

  // Additional dummy admin credentials
  const dummyAdminCredentials = [
    { staffId: "admin", password: "admin1234", role: "Admin", name: "System Admin" },
    { staffId: "ADMIN", password: "admin1234", role: "Admin", name: "Administrator" },
    { staffId: "admin@aizops", password: "admin1234", role: "Admin", name: "Admin User" },
    { staffId: "admin123", password: "admin1234", role: "Admin", name: "Admin123" }
  ];

  const showWelcomePopupMessage = (name, role) => {
    setWelcomePopup({ show: true, name, role });
    setTimeout(() => {
      setWelcomePopup({ show: false, name: '', role: '' });
    }, 3000);
  };

  const redirectToDashboard = () => {
    setTimeout(() => {
      navigate('/dashboard');
      onClose(true);
    }, 500);
  };

  const validate = () => {
    const newErrors = {};

    if (activeForm === "login") {
      if (!loginData.staffId.trim()) {
        newErrors.staffId = "Staff ID is required";
      }
      if (!loginData.password) {
        newErrors.password = "Password is required";
      }
    }

    if (activeForm === "signup") {
      if (!signupData.name.trim()) {
        newErrors.name = "Name is required";
      } else if (signupData.name.trim().length < 2) {
        newErrors.name = "Name must be at least 2 characters";
      }

      if (!signupData.staffId.trim()) {
        newErrors.staffId = "Staff ID is required";
      }

      if (!signupData.password) {
        newErrors.password = "Password is required";
      } else if (signupData.password.length < 4) {
        newErrors.password = "Password must be at least 4 characters";
      }

      if (signupData.password !== signupData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const fillDemoCredentials = (role) => {
    const creds = roleCredentials[role];
    if (creds) {
      setLoginData({ staffId: creds.staffId, password: creds.password });
      setErrors({});
    }
  };

  const fillAdminCredentials = () => {
    setLoginData({ staffId: "ADMIN001", password: "admin1234" });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      if (activeForm === "login") {
        // Check against role credentials
        let foundUser = null;
        let userRole = null;
        
        for (const [role, creds] of Object.entries(roleCredentials)) {
          if (creds.staffId === loginData.staffId && creds.password === loginData.password) {
            foundUser = creds;
            userRole = role;
            break;
          }
        }

        // Check against dummy admin credentials
        let dummyAdmin = null;
        if (!foundUser) {
          dummyAdmin = dummyAdminCredentials.find(
            creds => creds.staffId === loginData.staffId && creds.password === loginData.password
          );
          if (dummyAdmin) {
            foundUser = dummyAdmin;
            userRole = "admin";
          }
        }

        // Check for registered signup users in localStorage
        const registeredUsers = JSON.parse(localStorage.getItem('registeredUsers') || '[]');
        const registeredUser = registeredUsers.find(
          u => u.staffId === loginData.staffId && u.password === loginData.password
        );

        const user = registeredUser || foundUser;

        if (user) {
          const userData = {
            id: user.staffId,
            name: user.name || `${user.role} User`,
            staffId: user.staffId,
            role: user.role || (registeredUser ? registeredUser.role : userRole),
            userRole: user.role || (registeredUser ? registeredUser.role : userRole),
            position: user.position || getPositionByRole(user.role || userRole),
            department: user.department || getDepartmentByRole(user.role || userRole)
          };

          localStorage.clear();
          localStorage.setItem('token', `token_${userData.staffId}`);
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userRole', userData.role);
          localStorage.setItem('userStaffId', userData.staffId);

          setSuccess('Login successful!');
          showWelcomePopupMessage(userData.name, userData.role);
          
          setTimeout(() => {
            onClose(true);
            redirectToDashboard();
          }, 1500);
        } else {
          setErrors({ general: 'Invalid Staff ID or Password' });
        }
      } else {
        // Signup
        const registeredUsers = JSON.parse(localStorage.getItem('registeredUsers') || '[]');
        
        // Check if staff ID already exists
        if (registeredUsers.some(u => u.staffId === signupData.staffId)) {
          setErrors({ general: 'Staff ID already exists!' });
          setLoading(false);
          return;
        }

        const newUser = {
          name: signupData.name,
          staffId: signupData.staffId,
          password: signupData.password,
          role: signupData.role,
          registeredAt: new Date().toISOString()
        };

        registeredUsers.push(newUser);
        localStorage.setItem('registeredUsers', JSON.stringify(registeredUsers));

        setSuccess(`Account created successfully! Please login with your Staff ID: ${signupData.staffId}`);
        
        setTimeout(() => {
          setActiveForm('login');
          setSuccess('');
          setSignupData({ name: "", staffId: "", password: "", confirmPassword: "", role: "employee" });
        }, 2000);
      }
    } catch (error) {
      console.error('Auth error:', error);
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getPositionByRole = (role) => {
    const positions = {
      superadmin: "System Administrator",
      admin: "Administrator",
      manager: "Housekeeping Manager",
      hr: "HR Coordinator",
      employee: "Housekeeping Staff"
    };
    return positions[role] || "Staff Member";
  };

  const getDepartmentByRole = (role) => {
    const departments = {
      superadmin: "Executive Management",
      admin: "Administration",
      manager: "Housekeeping Department",
      hr: "Human Resources",
      employee: "Housekeeping Operations"
    };
    return departments[role] || "Housekeeping";
  };

  const switchForm = (form) => {
    setActiveForm(form);
    setErrors({});
    setSuccess('');
    if (form === "login") {
      setLoginData({ staffId: "", password: "" });
    } else {
      setSignupData({ name: "", staffId: "", password: "", confirmPassword: "", role: "employee" });
    }
  };

  const handleClose = () => {
    onClose(false);
    setActiveForm('login');
    setErrors({});
    setSuccess('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-blue-900/80 backdrop-blur-sm"
        onClick={handleClose}
      ></div>

      {/* Welcome Popup */}
      {welcomePopup.show && (
        <div className="fixed top-5 right-5 z-50 animate-slide-in-right">
          <div className="bg-blue-500 text-white px-6 py-4 rounded-lg shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-bold">Welcome {welcomePopup.name}!</p>
                <p className="text-sm opacity-90">Role: {welcomePopup.role.toUpperCase()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn mx-4">
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
        >
          ✕
        </button>

        {/* Content */}
        <div className="p-6 sm:p-8">
          
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">
              {activeForm === "login" ? "Welcome Back" : "Create Account"}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {activeForm === "login" 
                ? "Sign in to manage housekeeping tasks" 
                : "Join Housekeeping Management System"}
            </p>
          </div>

          {/* Success message */}
          {success && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              success.includes('Please login') 
                ? 'bg-blue-50 border border-blue-200 text-blue-700'
                : 'bg-green-50 border border-green-200 text-green-700'
            }`}>
              {success}
            </div>
          )}

          {/* Error message */}
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {errors.general}
            </div>
          )}

          {/* Login Form */}
          {activeForm === "login" && (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Staff ID</label>
                  <input
                    type="text"
                    name="staffId"
                    placeholder="Enter your Staff ID (e.g., ADMIN001, admin)"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      errors.staffId ? 'border-red-400' : 'border-gray-300'
                    } text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
                    value={loginData.staffId}
                    onChange={handleLoginChange}
                    disabled={loading}
                  />
                  {errors.staffId && (
                    <p className="text-red-500 text-xs mt-1">{errors.staffId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      errors.password ? 'border-red-400' : 'border-gray-300'
                    } text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
                    value={loginData.password}
                    onChange={handleLoginChange}
                    disabled={loading}
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Sign In'}
                </button>

                <p className="text-center text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:underline font-medium"
                    onClick={() => switchForm("signup")}
                  >
                    Sign up
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* Signup Form */}
          {activeForm === "signup" && (
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      errors.name ? 'border-red-400' : 'border-gray-300'
                    } text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
                    value={signupData.name}
                    onChange={handleSignupChange}
                    disabled={loading}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Staff ID</label>
                  <input
                    type="text"
                    name="staffId"
                    placeholder="Choose a Staff ID"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      errors.staffId ? 'border-red-400' : 'border-gray-300'
                    } text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
                    value={signupData.staffId}
                    onChange={handleSignupChange}
                    disabled={loading}
                  />
                  {errors.staffId && (
                    <p className="text-red-500 text-xs mt-1">{errors.staffId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Create a password (min 4 characters)"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      errors.password ? 'border-red-400' : 'border-gray-300'
                    } text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
                    value={signupData.password}
                    onChange={handleSignupChange}
                    disabled={loading}
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      errors.confirmPassword ? 'border-red-400' : 'border-gray-300'
                    } text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`}
                    value={signupData.confirmPassword}
                    onChange={handleSignupChange}
                    disabled={loading}
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Role</label>
                  <select
                    name="role"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    value={signupData.role}
                    onChange={handleSignupChange}
                    disabled={loading}
                  >
                    <option value="employee">Employee</option>
                    <option value="hr">HR</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                  <p className="text-gray-400 text-xs mt-1">Select your role in the organization</p>
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>

                <p className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:underline font-medium"
                    onClick={() => switchForm("login")}
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* Demo Credentials Section */}
          {/* <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500 mb-3">Demo Credentials</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => fillDemoCredentials('superadmin')}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition"
              >
                Super Admin
              </button>
              <button
                type="button"
                onClick={fillAdminCredentials}
                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                Admin (admin1234)
              </button>
              <button
                type="button"
                onClick={() => fillDemoCredentials('manager')}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition"
              >
                Manager
              </button>
              <button
                type="button"
                onClick={() => fillDemoCredentials('hr')}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition"
              >
                HR
              </button>
              <button
                type="button"
                onClick={() => fillDemoCredentials('employee')}
                className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded hover:bg-gray-100 transition col-span-2"
              >
                Employee
              </button>
            </div>
            <p className="text-xs text-center text-gray-400 mt-3">
              Admin credentials: <strong>Staff ID: ADMIN001</strong> | <strong>Password: admin1234</strong>
            </p>
          </div> */}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AuthModal;